import { Button, Page, TextStyle } from '@superops/ui-design-system';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { RMMClient, useCachedQuery } from '@nile/graphql';
import {
  GET_MONITORED_ASSETS,
  SOFT_DELETE_ASSETS
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ALL_TABLES,
  SuperTable,
  TableContainer,
  getColumnWidth as superTableColumnWidth,
  getTableRowCount,
  getTranslatedText,
  getAllColumnWidth,
  isAuthorised,
  AppContext,
  toast
} from '@nile/components';
import {
  getAsyncOptions,
  fetchValues,
  updateConditions,
  getDefaultValue,
  generateOptions
} from '@nile/banyan/src/Pebbles/IntegrationsHelper';
import {
  getAccessor,
  getAssetColConfig,
  isEditable
} from '@nile/banyan/src/Pages/Protected/Configuration/Pricing/helper';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { columnsConfig } from '@nile/rmm/src/modules/asset-management/components/sub-pages/all-monitored-assets-view/all-monitored-assets-view-helper';
import {
  getDynamicQuery,
  checkDynamicQuery,
  getVariables
} from '@nile/rmm/src/modules/asset-management/components/sub-pages/all-monitored-assets-view/all-monitored-assets-view-filter-helper';
import { GET_NETWORK_MONITORING_ASSET_CLASSES } from '@nile/rmm/src/snmp/SnmpQuery';
import { isArray, isEmpty } from 'lodash';

const ManageAssetModal = (props) => {
  const { closePage = () => {}, callBack } = props;

  const intl = useIntl();

  const tableName = ALL_TABLES.manageAsset;
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const userRole = currentUser?.role?.roleFeatureList || [];
  const isDeleteAuthorised = isAuthorised(userRole, 'asset.delete');

  const { data: networkAssets, loading: networkAssetsLoading } = useCachedQuery(
    GET_NETWORK_MONITORING_ASSET_CLASSES,
    {
      client: RMMClient,
      variables: {}
    }
  );
  const ROW_COUNT = getTableRowCount(tableName);

  const options = {
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ],
    condition: {
      joinOperator: 'AND',
      operands: [
        {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'assetClass.classId',
              operator: 'includes',
              value: networkAssets?.getNetworkMonitoringAssetClasses?.map(
                (el) => el.classId
              )
            }
          ]
        }
      ]
    },
    view: {
      id: '7001'
    }
  };

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);

  const [listInfo, setListInfo] = useState(options);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const columnWidthObject = getAllColumnWidth(tableName);
  const [viewConditions, setViewConditions] = useState(null);
  const [records, setRecords] = useState([]);
  const conditionRef = useRef(1);
  conditionRef.current = viewConditions;

  const updateAllConditions = (condition, selectedField) => {
    updateConditions({
      condition,
      selectedField,
      viewConditions: conditionRef.current,
      setConditions: setViewConditions
    });
  };

  const fetchViewData = async (_, pageOptions) => {
    setLoading(true);
    let cond = !!viewConditions
      ? viewConditions
      : pageOptions?.condition || options?.condition;

    cond = !!cond
      ? cond
      : cond?.operands?.push({
          attribute: 'assetClass.classId',
          operator: 'includes',
          value: networkAssets?.getNetworkMonitoringAssetClasses?.map(
            (el) => el.classId
          )
        });

    const requestOption = {
      listInfo: pageOptions
        ? {
            page: pageOptions.page || listInfo?.page || options.page,
            pageSize:
              pageOptions.pageSize || listInfo?.pageSize || options.pageSize,
            sort: pageOptions.sort || null,
            condition: cond,
            view: {
              id: '7001'
            }
          }
        : {
            ...options,
            condition: cond
          }
    };
    await RMMClient.query({
      query: GET_MONITORED_ASSETS,
      variables: { ...requestOption, viewType: 'ALL_MONITORED_ASSET' },
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data) {
        setData(res?.data?.getMonitoredAssets?.assets);
        const {
          page,
          pageSize,
          totalCount,
          sort
        } = res?.data?.getMonitoredAssets?.listInfo;
        setListInfo({
          totalCount: totalCount,
          pageSize: pageSize,
          page: page,
          sort: sort
        });
      }
    });
    const cols = [
      {
        name: 'id',
        label: '',
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: true,
        disableFilters: true,
        size: 25
      },
      {
        name: 'ipAddress',
        label: getTranslatedText(intl, 'asset.defaultField.primaryIp'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'name',
        label: getTranslatedText(intl, 'admin.employee.name.title'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'assetMake.name',
        label: getTranslatedText(intl, 'attribute.manufacturer'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: true
      },
      {
        name: 'assetClass.name',
        label: getTranslatedText(intl, 'attribute.assetClassName'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'assetModel.name',
        label: getTranslatedText(intl, 'attribute.model'),
        dataType: 'string',
        uiDataType: 'text',
        filterType: 'text',
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'client.name',
        label: getTranslatedText(intl, 'attribute.clientId'),
        dataType: 'string',
        filterType: 'select',
        disableSortBy: false,
        disableFilters: false
      },
      {
        name: 'site.name',
        label: getTranslatedText(intl, 'attribute.siteId'),
        dataType: 'string',
        filterType: 'select',
        disableSortBy: false,
        disableFilters: false
      }
    ];
    const columns = cols.map((col, idx) => {
      return {
        Header: col.label,
        size: superTableColumnWidth(columnWidthObject, col.name, col?.size),
        minSize: 30,
        accessorKey: getAccessor(col),
        type: getAssetColConfig(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        helpers: {
          getDefaultValue,
          checkDynamicQuery,
          updateConditions: updateAllConditions,
          getVariables: getVariables.bind(this, col),
          getDynamicQuery,
          generateOptions,
          fetchValues,
          getAsyncOptions,
          entity: 'Asset'
        },
        disableFilters: col?.disableFilters,
        disableSortBy: col?.disableSortBy,
        field: col
      };
    });
    if (isDeleteAuthorised) {
      columns.push({
        Header: ' ',
        accessorKey: 'value',
        size: 35,
        id: 'delete',
        type: 'Action',
        action: true,
        disableSortBy: true,
        disableFilters: true,
        actions: {
          onClick: deleteAsset
        },
        variant: 'confirmation',
        sectioned: true,
        labels: {
          description: 'admin.pricing.list.asset.delete.modalDescription',
          title: 'admin.pricing.list.asset.delete.title'
        },
        field: { name: 'delete' }
      });
    }
    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    setLoading(false);
  };

  const deleteAsset = (items = []) => {
    if (isEmpty(items)) return;
    RMMClient.mutate({
      mutation: SOFT_DELETE_ASSETS,
      variables: {
        assetIds: isArray(items)
          ? items?.map((item) => item.assetId)
          : items?.assetId
      }
    })
      .then((res) => {
        if (res && res.data) {
          if (res?.data?.softDeleteAssets?.status === 'FAILURE') {
            toast('error', 'rmm.assetManagement.bulk.deleteAsset.errorMessage');

            setRecords([]);
          } else if (
            res?.data?.softDeleteAssets?.status === 'PARTIALLY_SUCCESS'
          ) {
            toast('warning', 'rmm.asset.delete.partial.success.message', 'en', {
              successCount: res?.data?.softDeleteAssets?.successCount,
              failedCount: res?.data?.softDeleteAssets?.failureCount
            });
            fetchViewData();
            setRecords([]);
          } else {
            toast('success', 'rmm.assetManagement.bulk.deleteAsset.successMsg');
            fetchViewData();
            setRecords([]);
          }
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    if (!isEmpty(networkAssets)) {
      fetchViewData(null, {
        ...options,
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'assetClass.classId',
                  operator: 'includes',
                  value: networkAssets?.getNetworkMonitoringAssetClasses?.map(
                    (el) => el.classId
                  )
                }
              ]
            }
          ]
        }
      });
    }
  }, [networkAssets]);

  useEffect(() => {
    if (!isEmpty(networkAssets)) {
      fetchViewData(null, viewConditions);
    }
  }, [viewConditions]);

  return (
    <>
      <Page
        contentHeight="100px"
        variant="seondary"
        divider
        fullWidth
        title={
          <div className="fx actr g10">
            <Button
              icon={<SVGIcon name="BackArrow" size="sm" />}
              onClick={() => closePage('NETWORK_ASSET')}
            />
            <TextStyle weight="Bold">
              <FormattedMessage id="app.purchase.manage.asset" />
            </TextStyle>
          </div>
        }
        primaryAction={
          <div className="fx g20">
            {viewConditions && (
              <Button plain onClick={() => setViewConditions(null)} size="tiny">
                <FormattedMessage id="attribute.reset" />
              </Button>
            )}
            {!isEmpty(records) && (
              <>
                <Button
                  icon={<SVGIcon name="delete" />}
                  onClick={() => deleteAsset(records)}
                  loading={loading}
                >
                  {getTranslatedText(intl, 'app.delete.btn')}
                </Button>
              </>
            )}
          </div>
        }
      >
        <div className="manageModal">
          <TableContainer>
            <SuperTable
              context={{
                setRecords: setRecords
              }}
              fullHeight
              columns={columnGroup}
              data={data}
              rowCount={ROW_COUNT}
              defaultColumnCells={columnsConfig}
              refreshTable={fetchViewData}
              tableName={tableName}
              listInfo={listInfo}
              pagination={
                Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1
              }
              pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
              recordCount={listInfo.totalCount}
              activePage={listInfo.page || 0}
              isLoading={loading || networkAssetsLoading}
            />
          </TableContainer>
        </div>
      </Page>
    </>
  );
};

export default ManageAssetModal;
